import * as React from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';

import { useTracker } from '@shared/components/google_analytics/use_tracker';

import { Pageview } from '@portal/components/pageview';
import { Layout } from '@portal/components/shared/layout';
import { RouteWithTitle } from '@portal/components/shared/route_with_title';
import {
  accountURL,
  activateURL,
  addressesURL,
  billingURL,
  claimsURL,
  downsizeURL,
  invoiceURL,
  confirmOrderURL,
  ordersURL,
  paymentPlansURL,
  payURL,
  referralsURL,
  rescheduleOfferURL,
  reviewURL,
  settingsURL,
  supportURL,
  itemsURL,
  itemsWithConditionNotesURL,
  signedOrderSignaturesURL,
  DEFAULT_ITEMS_STATUS_SLUG,
  loginURL,
  planURL,
  renewURL,
  rotatePasswordURL,
} from '@portal/config/routes';

import { Account } from './account';
import { Addresses } from './addresses';
import { Billing } from './billing';
import { Claims } from './claims';
import { Config } from './config';
import { DownsizeForm as PlanDownsizeForm } from './downsize/form';
import { Detail as InvoiceDetail } from './invoices/detail';
import { ConditionNotesSummary } from './items/condition_notes/summary_page';
import { List as ItemsList } from './items/list';
import { Login } from './login';
import { Orders } from './orders';
import { Confirm as OrderConfirm } from './orders/confirm';
import { SignedOrderSignatures } from './orders/signatures';
import { Activate } from './password/activate';
import { RotatePassword } from './password/rotate';
import { PayInvoices } from './pay/pay_invoices';
import { PaymentPlans } from './payment_plans';
import { Plan } from './plan';
import { RenewalRenew } from './renewals/renew';
import { Details as RescheduleOfferDetails } from './reschedule_offer/details';
import { Review } from './review';
import { Rewards } from './rewards/rewards';
import { Settings } from './settings';
import { Support } from './support';

export const App: React.FC = () => (
  <Config>
    <Router>
      <AppContent />
    </Router>
  </Config>
);
App.displayName = 'App';

const AppContent: React.FC = () => {
  useTracker({});

  return (
    <Layout>
      <Pageview />
      <Switch>
        <RouteWithTitle exact path={accountURL()} component={Account} title="Account Details" />
        <RouteWithTitle path={activateURL()} component={Activate} title="Activate" />
        <Route path={addressesURL()} component={Addresses} />
        <Route path={billingURL()} component={Billing} />
        <Route path={claimsURL()} component={Claims} />
        <Route path={invoiceURL(':invoiceID')} component={InvoiceDetail} />
        <RouteWithTitle
          exact
          path={itemsWithConditionNotesURL()}
          component={ConditionNotesSummary}
          title="Items with Condition Notes"
        />
        <RouteWithTitle exact path={itemsURL(':status')} component={ItemsList} title="Items" />
        <Redirect from={itemsURL()} to={itemsURL(DEFAULT_ITEMS_STATUS_SLUG)} />
        <RouteWithTitle exact path={confirmOrderURL(':token')} component={OrderConfirm} title="Confirm Appointment" />
        <RouteWithTitle
          exact
          path={signedOrderSignaturesURL()}
          component={SignedOrderSignatures}
          title="Sign Documents"
        />
        <Route path={ordersURL()} component={Orders} />
        <RouteWithTitle path={paymentPlansURL()} component={PaymentPlans} title="Payment Plans" />
        <RouteWithTitle path={payURL()} component={PayInvoices} title="Pay your Balance" />
        <RouteWithTitle path={planURL()} component={Plan} title="Update Plan" />
        <Route path={reviewURL()} component={Review} />
        <RouteWithTitle path={supportURL()} component={Support} title="Support" />
        <RouteWithTitle path={settingsURL()} component={Settings} title="Settings" />
        <RouteWithTitle path={referralsURL()} component={Rewards} title="Rewards" />
        <RouteWithTitle path={downsizeURL()} component={PlanDownsizeForm} title="Downsize" />
        <Route path={loginURL()} component={Login} />
        <Route path={rescheduleOfferURL()} component={RescheduleOfferDetails} />
        <Route path={renewURL()} component={RenewalRenew} />
        <Route path={rotatePasswordURL()} component={RotatePassword} />
      </Switch>
    </Layout>
  );
};
AppContent.displayName = 'AppContent';
